import {
  Button,
  Divider,
  Empty,
  Flex,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography
} from 'antd';
import {
  COMPLEMENTARY_ACTIVITY_STATUS_MAP,
  approveComplementaryActivity,
  createComplementaryActivity,
  deleteComplementaryActivity,
  downloadComplementaryActivitiesReport,
  getComplementaryActivityHours,
  listComplementaryActivities,
  rejectComplementaryActivity,
  updateComplementaryActivity,
  uploadComplementaryActivityFile
} from 'services/graduate-enrollment/complementary-activities';
import { ComplementaryActivityCard } from './ComplementaryActivityCard';
import {
  ComplementaryActivityFormModal,
  ComplementaryActivityFormSubmit
} from './ComplementaryActivityFormModal';
import { ComplementaryActivityHoursSummary } from './ComplementaryActivityHoursSummary';
import {
  ComplementaryActivityStatus,
  IComplementaryActivity,
  IComplementaryActivityHours
} from 'interfaces/complementary_activities';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { pluralize } from 'utils/pluralize';
import { useCallback, useEffect, useState } from 'react';
import { useCan, useNotification } from '@refinedev/core';

interface TabComplementaryActivitiesProps {
  enrollmentId: string;
  enrollmentCreatedAt?: string;
}

const STATUS_OPTIONS = Object.entries(COMPLEMENTARY_ACTIVITY_STATUS_MAP).map(
  ([value, { text }]) => ({ value, label: text })
);

export const TabComplementaryActivities = ({
  enrollmentId,
  enrollmentCreatedAt
}: TabComplementaryActivitiesProps) => {
  const { open } = useNotification();
  const [status, setStatus] = useState<ComplementaryActivityStatus>();
  const [data, setData] = useState<IComplementaryActivity[]>([]);
  const [hours, setHours] = useState<IComplementaryActivityHours | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<IComplementaryActivity | null>(null);
  const [rejecting, setRejecting] = useState<IComplementaryActivity | null>(
    null
  );
  const [feedback, setFeedback] = useState('');

  // Ver a matrícula basta para listar; as ações exigem `complementary_activities`.
  // Espelha `Admin::Graduate::Courses::ComplementaryActivityPolicy` do Atlas.
  const { data: canCreate } = useCan({
    resource: 'complementary_activities',
    action: 'create'
  });
  const { data: canEdit } = useCan({
    resource: 'complementary_activities',
    action: 'edit'
  });
  const { data: canApprove } = useCan({
    resource: 'complementary_activities',
    action: 'approve'
  });
  const { data: canReject } = useCan({
    resource: 'complementary_activities',
    action: 'reject'
  });
  const { data: canDelete } = useCan({
    resource: 'complementary_activities',
    action: 'delete'
  });

  const allowCreate = Boolean(canCreate?.can);
  const allowEdit = Boolean(canEdit?.can);
  const allowApprove = Boolean(canApprove?.can);
  const allowReject = Boolean(canReject?.can);
  const allowDelete = Boolean(canDelete?.can);

  const notifyError = useCallback(
    (description: string) =>
      open?.({ type: 'error', message: 'Ops!', description }),
    [open]
  );

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      // As horas não dependem do filtro: sempre a matrícula inteira.
      const [activities, enrollmentHours] = await Promise.all([
        listComplementaryActivities(enrollmentId, status),
        getComplementaryActivityHours(enrollmentId)
      ]);
      setData(activities);
      setHours(enrollmentHours);
    } catch {
      notifyError('Erro ao carregar as atividades acadêmicas complementares.');
    } finally {
      setIsLoading(false);
    }
  }, [enrollmentId, status, notifyError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true);
      await downloadComplementaryActivitiesReport(enrollmentId, status);
    } catch {
      notifyError('Erro ao baixar o CSV das AACs.');
    } finally {
      setIsDownloading(false);
    }
  }, [enrollmentId, status, notifyError]);

  const handleSubmit = useCallback(
    async ({ payload, file }: ComplementaryActivityFormSubmit) => {
      if (!editing && !file) {
        notifyError('Selecione o arquivo da atividade.');
        return;
      }

      try {
        setIsSaving(true);
        const fileData = file
          ? await uploadComplementaryActivityFile(enrollmentId, file)
          : {};

        await (editing
          ? updateComplementaryActivity(enrollmentId, editing.id, {
              ...payload,
              ...fileData
            })
          : createComplementaryActivity(enrollmentId, {
              ...payload,
              ...fileData
            }));

        open?.({ type: 'success', message: 'AAC salva com sucesso.' });
        setFormOpen(false);
        setEditing(null);
        fetchData();
      } catch {
        notifyError('Erro ao salvar a AAC.');
      } finally {
        setIsSaving(false);
      }
    },
    [editing, enrollmentId, fetchData, notifyError, open]
  );

  const handleApprove = useCallback(
    (activity: IComplementaryActivity) => {
      Modal.confirm({
        title: 'Aprovar AAC',
        content: `As ${activity.hours}h de "${activity.event_name}" passam a contar como cumpridas.`,
        okText: 'Aprovar',
        cancelText: 'Cancelar',
        onOk: async () => {
          try {
            await approveComplementaryActivity(enrollmentId, activity.id);
            open?.({ type: 'success', message: 'AAC aprovada.' });
            fetchData();
          } catch {
            notifyError('Erro ao aprovar a AAC.');
          }
        }
      });
    },
    [enrollmentId, fetchData, notifyError, open]
  );

  const handleDelete = useCallback(
    (activity: IComplementaryActivity) => {
      Modal.confirm({
        title: 'Excluir AAC',
        content: `"${activity.event_name}" será excluída e não aparecerá mais para o aluno.`,
        okText: 'Excluir',
        okButtonProps: { danger: true },
        cancelText: 'Cancelar',
        onOk: async () => {
          try {
            await deleteComplementaryActivity(enrollmentId, activity.id);
            open?.({ type: 'success', message: 'AAC excluída.' });
            fetchData();
          } catch {
            notifyError('Erro ao excluir a AAC.');
          }
        }
      });
    },
    [enrollmentId, fetchData, notifyError, open]
  );

  const handleReject = useCallback(async () => {
    if (!rejecting) {
      return;
    }

    try {
      setIsSaving(true);
      await rejectComplementaryActivity(enrollmentId, rejecting.id, feedback);
      open?.({ type: 'success', message: 'AAC rejeitada.' });
      setRejecting(null);
      setFeedback('');
      fetchData();
    } catch {
      notifyError('Erro ao rejeitar a AAC.');
    } finally {
      setIsSaving(false);
    }
  }, [enrollmentId, feedback, fetchData, notifyError, open, rejecting]);

  const openEdit = (activity: IComplementaryActivity) => {
    setEditing(activity);
    setFormOpen(true);
  };

  const openReject = (activity: IComplementaryActivity) => {
    setFeedback('');
    setRejecting(activity);
  };

  return (
    <div>
      <Typography.Text type="secondary" className="block mb-4">
        Gerencie as atividades acadêmicas complementares do aluno, aprovando ou
        rejeitando as solicitações pendentes. Apenas as atividades aprovadas
        contabilizam horas cumpridas no histórico escolar.
      </Typography.Text>

      <ComplementaryActivityHoursSummary hours={hours} />

      <Divider />

      <Flex
        justify="space-between"
        align="center"
        wrap
        gap={8}
        className="mb-4"
      >
        <Select
          allowClear
          placeholder="Filtrar por status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={value => setStatus(value)}
          style={{ minWidth: 200 }}
        />

        <Space>
          <Button
            icon={<DownloadOutlined />}
            loading={isDownloading}
            disabled={isLoading || data.length === 0}
            onClick={handleDownload}
          >
            Baixar CSV
          </Button>
          {allowCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Cadastrar AAC
            </Button>
          )}
        </Space>
      </Flex>

      <Spin spinning={isLoading}>
        {data.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nenhuma AAC cadastrada"
          />
        ) : (
          <Space direction="vertical" size="middle" className="w-full">
            <Typography.Text strong>
              {pluralize(data.length, 'atividade', 'atividades')}
            </Typography.Text>
            {data.map(activity => (
              <ComplementaryActivityCard
                key={activity.id}
                enrollmentId={enrollmentId}
                activity={activity}
                allowEdit={allowEdit}
                allowApprove={allowApprove}
                allowReject={allowReject}
                allowDelete={allowDelete}
                onEdit={openEdit}
                onApprove={handleApprove}
                onReject={openReject}
                onDelete={handleDelete}
              />
            ))}
          </Space>
        )}
      </Spin>

      <ComplementaryActivityFormModal
        open={formOpen}
        activity={editing ?? undefined}
        enrollmentCreatedAt={enrollmentCreatedAt}
        loading={isSaving}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <Modal
        title="Rejeitar AAC"
        open={Boolean(rejecting)}
        okText="Rejeitar"
        okButtonProps={{ danger: true, disabled: !feedback.trim() }}
        cancelText="Cancelar"
        confirmLoading={isSaving}
        onOk={handleReject}
        onCancel={() => setRejecting(null)}
      >
        <Typography.Paragraph type="secondary">
          O motivo aparece para o aluno, que pode cadastrar outra AAC.
          {rejecting?.status === 'approved' &&
            ` As ${rejecting.hours}h desta AAC deixam de contar como cumpridas.`}
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          placeholder="Motivo da rejeição"
          value={feedback}
          onChange={event => setFeedback(event.target.value)}
        />
      </Modal>
    </div>
  );
};
